import React, { useMemo } from "react";
import { Checkbox, Badge } from "@components";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { cn } from "@utils";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/16/solid";
import { formatAmount } from "@utils";

export const InvoiceGroup = ({
  group,
  selectedIds,
  toggleGroup,
  ids,
  children,
  invoicesRequestedAreMandatory,
  allInvoicesDisputed = false,
  discountedSelectedCount = 0,
}) => {
  const { required, invoices, creditNotes = [], name } = group;

  const allChecked = useMemo(
    () => ids.length > 0 && ids.every((id) => selectedIds.includes(id)),
    [ids, selectedIds],
  );
  const partChecked = useMemo(
    () => selectedIds.length > 0 && selectedIds.length < ids.length,
    [ids, selectedIds],
  );

  const groupTotal = useMemo(
    () =>
      invoices
        .reduce((s, i) => s + i.payableAmount, 0)
        .toLocaleString("en-GB", { minimumFractionDigits: 2 }),
    [invoices],
  );

  // Credit notes count as group items too — a credit-note-heavy group otherwise reads
  // "0 selected / See invoices (0)" while showing a non-zero amount.
  const selectedInvoiceCount = useMemo(
    () =>
      selectedIds.filter(
        (id) =>
          invoices.some((inv) => inv.ledgerTransactionId === id) ||
          creditNotes.some((cn) => cn.ledgerTransactionId === id),
      ).length,
    [selectedIds, invoices, creditNotes],
  );

  return (
    <Disclosure key={name}>
      {({ open }) => (
        <div
          className={cn(
            "relative z-0 border border-zinc-200 rounded-xl bg-white",
            !open && "shadow-[0px_1px_4px_0px_rgba(0,0,0,0.10)]",
          )}
        >
          <>
            <div
              className={cn(
                "flex items-start p-4 relative z-10",
                open && "border-b border-zinc-200",
              )}
            >
              <div className="relative group/dispute w-12 shrink-0">
                <label className="p-4 -mt-3 -ml-4 -mr-2 cursor-pointer flex items-center justify-center">
                  <Checkbox
                    checked={allChecked || partChecked}
                    partChecked={partChecked}
                    onChange={() => toggleGroup(ids)}
                    readOnly={
                      (invoicesRequestedAreMandatory && required) ||
                      allInvoicesDisputed
                    }
                  />
                </label>
                {allInvoicesDisputed && (
                  <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/dispute:block w-max max-w-52 rounded-lg bg-zinc-800 px-2.5 py-1.5 text-xs text-white shadow-lg z-50 text-center leading-snug whitespace-normal">
                    All invoices are disputed — this bucket cannot be selected
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-800" />
                  </div>
                )}
              </div>
              <DisclosureButton
                as="div"
                className="flex flex-col grow cursor-pointer gap-2"
              >
                <div className="flex justify-between items-end gap-2">
                  <span className="font-normal flex flex-wrap-reverse items-center gap-x-2 gap-y-1 min-w-0">
                    {name}
                    {required && (
                      <Badge color="blue">
                        {invoicesRequestedAreMandatory
                          ? "Required"
                          : "Requested"}
                      </Badge>
                    )}
                  </span>
                  <span className="font-normal text-right">
                    {formatAmount(group.total)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs/6 gap-2">
                  <span className="text-zinc-500 font-light whitespace-nowrap">
                    {`${selectedInvoiceCount} selected`}
                  </span>
                  <span className="flex items-center gap-1 text-primary-600">
                    <span className="whitespace-nowrap">
                      {`See invoices (${invoices.length + creditNotes.length})`}
                    </span>
                    <ChevronDownIcon
                      className={cn(
                        "h-4 w-4 transition-transform text-inherit",
                        open && "rotate-180",
                      )}
                    />
                  </span>
                </div>
                {discountedSelectedCount > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1 w-fit">
                    <CheckCircleIcon className="size-3.5 shrink-0" />
                    <span>
                      Settlement discount applied &middot;{" "}
                      {discountedSelectedCount}{" "}
                      {discountedSelectedCount !== 1 ? "invoices" : "invoice"}
                    </span>
                  </div>
                )}
              </DisclosureButton>
            </div>
            <DisclosurePanel
              className="relative z-0 origin-top transition-all duration-100 ease-out data-closed:-translate-y-1 data-closed:gap-0 data-closed:opacity-0 px-4 bg-zinc-50 shadow-[inset_0px_4px_4px_0px_rgba(0,0,0,0.05)] flex flex-col gap-4 divide-y divide-zinc-200 overflow-hidden rounded-b-xl"
              transition={true}
            >
              {children}
            </DisclosurePanel>
          </>
        </div>
      )}
    </Disclosure>
  );
};
